'use client';

import type { Project } from '@/app/models/overhead';
import type { Provider } from '@/app/models/providersResponse';
import { useSearchProjectsQuery } from '@/app/services/projectApi';
import { useResourceDonorAllQuery } from '@/app/services/resourceDonors';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import LateralDrawer from '@/components/common/LateralDrawer';
import PageTitle from '@/components/common/pageTitle';
import TextButton from '@/components/common/TextButton';
import NewEditResourceDonors from '@/components/resourceDonors/NewEditResourceDonors';
import ResourceDonorsTable from '@/components/resourceDonors/ResourceDonorsTable';
import { Box, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

function ResourceDonor() {
  const theme = useTheme();
  const { t } = useTranslation(['resourceDonor', 'common']);
  const [drawerStatus, setDrawerStatus] = useState(false);

  const [selectedDestinationItem, setSelectedDestinationItem] = useState<Provider | null>(null);
  const [selectedOriginItem, setSelectedOriginItem] = useState<Provider | null>(null);

  const [selectedAllocationPercent, setSelectedAllocationPercent] = useState<string | null>(null);
  const [selectedEligiblePercent, setSelectedEligiblePercent] = useState<string | null>(null);

  const [originProject, setOriginProject] = useState<Project | null>(null);
  const [originProjectSearch, setOriginProjectSearch] = useState('');
  const { data: originProjects, isFetching: isLoadingOrigin } = useSearchProjectsQuery(
    {
      searchTerm: originProjectSearch,
      bringClosed: false,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: originProjectSearch.length < 1 },
  );

  const { data: resourceDonorsAll } = useResourceDonorAllQuery(originProject?.code || '');

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        mr: 2,
      }}
    >
      <PageTitle
        title={t('resource.donors')}
        titleLabel={` (${t('by.client')})`}
        subtitle={`${t('resource.donors.subtitle')}`}
      />
      <Typography component="p" variant="fieldHeader">
        {t('common:project')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          mb: 2,
          alignItems: 'center',
        }}
      >
        <GenericAutoComplete
          value={originProject}
          onChange={(value) => {
            setOriginProject(value);
          }}
          equalityTest={(option, value) => option?.code === value?.code}
          getOptionLabel={(option) =>
            `${option?.code} - ${option?.name}${option?.branchName ? ` (${option?.branchName})` : ''}`
          }
          onSearch={(value) => {
            setOriginProjectSearch(value);
          }}
          options={originProjectSearch !== '' && originProjects?.content ? originProjects?.content : []}
          placeholder={t('common:enter.project.name.or.code') as string}
          loading={isLoadingOrigin}
          sx={{ width: theme.spacing(50) }}
        />
        <TextButton
          sx={{ ml: 'auto' }}
          text={`+ ${t('new.donor')}`}
          onClick={() => {
            setSelectedDestinationItem(null);
            setSelectedAllocationPercent(null);
            setSelectedEligiblePercent(null);
            setSelectedOriginItem(null);
            setDrawerStatus(true);
          }}
        />
      </Box>

      <ResourceDonorsTable
        setSelectedAllocationPercent={setSelectedAllocationPercent}
        setSelectedItem={setSelectedDestinationItem}
        setDrawerOpen={setDrawerStatus}
        rows={resourceDonorsAll || []}
        setSelectedOriginItem={setSelectedOriginItem}
        setSelectedEligiblePercent={setSelectedEligiblePercent}
      />

      <LateralDrawer open={drawerStatus} setOpen={setDrawerStatus} position="right" size="bigger">
        <NewEditResourceDonors
          editMaxAllocationPercent={selectedAllocationPercent as string}
          editEligiblePercent={selectedEligiblePercent as string}
          editDestinationProject={selectedDestinationItem as Provider}
          setEditDestinationProject={setSelectedDestinationItem}
          editEditOriginProject={selectedOriginItem as Provider}
          setEditOriginProject={setSelectedOriginItem}
          setDrawerOpen={setDrawerStatus}
        />
      </LateralDrawer>
    </Box>
  );
}

export default ResourceDonor;
